import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { wait, UpgradeLoggerService } from 'ngx-core-services';
import { IonPopover } from '@ionic/angular';
import { ToastFacade } from 'ngx-zcs-components';
import {
  FirmwareComparison,
  compareInverterFWModuleVersions,
  ComposeV3UpgradeInfo,
  V3UpgradeInfo,
  BYPASS_MIN_VERSION,
} from 'ngx-modbus';

import { AccessSupportService } from './access-support.service';
import { SafetyService } from './safety.service';
import { ChannelHandler } from '.';
import { SgpInteraction, SgpInteractionWithLogging, UpdateResult, UpgradeMultiModulesProgress, UpgradeState, UpgradeWizardState } from '../SGP';

const SGP_DEBUG = { ENABLED: true };
const sgpLog = (msg: string, data?: unknown) => SGP_DEBUG.ENABLED && console.log(`[Modbus:UpgradeSGP] ${msg}`, data ?? '');

export interface DeviceModuleInfo {
  serial: string;
  modules: { [moduleName: string]: string }; // Key: Module name, Value: Current version
}

export const MemoizationPromiseFnBuilder = <T extends Array<any>, U>(
  fnToCall: (...args: T) => Promise<U>,
  expirationTime: number = 0,
  context?: any,
) => {
  const cache = new Map<string, { value: U; expirationTime: number }>();
  return async (...args: T): Promise<U> => {
    const key = JSON.stringify(args);
    const cached = cache.get(key);
    if (cached && cached.expirationTime > Date.now()) {
      return Promise.resolve(cached.value);
    }
    const value = context ? await fnToCall.apply(context, args) : await fnToCall(...args);
    cache.set(key, { value, expirationTime: Date.now() + expirationTime });
    return value;
  };
};


const fetchCacheTime = 60000 * 5;
const memoizedFetch = MemoizationPromiseFnBuilder(async (url: string) => {
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data = await response.arrayBuffer();
  return data;
}, fetchCacheTime);

@Injectable({
  providedIn: 'root',
})
export class UpgradeSGPService {
  private readonly compareVersions = compareInverterFWModuleVersions;
  cancellationSignal = { cancelled: false };

  readonly state$ = new BehaviorSubject<UpgradeWizardState>({
    currentPhase: 0,
    errorMessage: null,
    isUpdating: false,
    totalProgress: 0,
    updatePhases: [],
    upgradeConfig: null,
    inverterUpgradeTypes: null,
    batteryUpgradeTypes: null,
    safetyMetadata: null,
    safetyList: [],
    filteredSafety: [],
    selectedSafety: '',
    selectedSafetyIndex: null,
    multiModules: null,
  });

  get state() {
    return this.state$.value;
  }

  constructor(
    private readonly accessSupportService: AccessSupportService,
    private readonly channelHandler: ChannelHandler,
    private readonly safetyService: SafetyService,
    private readonly toastFacade: ToastFacade,
    private readonly upgradeLogger?: UpgradeLoggerService,
  ) {
    this.downloadSafety = this.downloadSafety.bind(this);
    this.updateSafety = this.updateSafety.bind(this);
    this.updateModule = this.updateModule.bind(this);
    this.updateProgress = this.updateProgress.bind(this);
  }

  async isDeviceUpgradable(device: DeviceModuleInfo): Promise<UpgradeState> {
    const deviceConfig = await this.accessSupportService.getDeviceModuleInfo(device.serial);

    sgpLog('isDeviceUpgradable called for serial:', device.serial);
    sgpLog('device.modules (raw from device):', JSON.stringify(device.modules));

    if (!deviceConfig || deviceConfig.DEVICE_MODEL_ID !== 'INVERTER') {
      sgpLog('deviceConfig not found or not INVERTER, returning not upgradable');
      return {
        upgradable: false,
        isUpdated: null,
        upgradePossible: null,
        upgradeLinks: null,
        latestVersions: null,
        safetyCustom: [],
      };
    }

    sgpLog('deviceConfig (INVERTER):', JSON.stringify({
      ID: deviceConfig.ID,
      DEVICE_MODEL_ID: deviceConfig.DEVICE_MODEL_ID,
      ARM: deviceConfig.ARM,
      DSPM: deviceConfig.DSPM,
      DSPS: deviceConfig.DSPS,
    }));

    const upgradeLinks: { [moduleName: string]: string | null } = {};
    const isUpdated: { [moduleName: string]: boolean } = {};
    const upgradePossible: { [moduleName: string]: boolean } = {};
    let upgradable = false;

    const fwModule = (m: 'ARM' | 'DSPM' | 'DSPS' | 'SAFETY') => {
      const configEntries = deviceConfig[m];
      sgpLog(`fwModule('${m}') - configEntries:`, JSON.stringify(configEntries));
      sgpLog(`fwModule('${m}') - device.modules['${m}'] raw value:`, JSON.stringify(device.modules[m]));

      const result = configEntries?.filter((module) => {
        if (m === 'SAFETY') {
          return true;
        }

        let chipId = module.LATEST_VERSION.replace(/^0+/, '');
        chipId = chipId.length ? chipId[0] : '';

        let deviceChipId = device.modules[m].replace(/^0+/, '');
        deviceChipId = deviceChipId.length ? deviceChipId[0] : '';

        const match = chipId === deviceChipId;
        sgpLog(`fwModule('${m}') filter: config LATEST_VERSION='${module.LATEST_VERSION}' → chipId='${chipId}', device version='${device.modules[m]}' → deviceChipId='${deviceChipId}', match=${match}`);
        return match;
      }) ?? [null];

      sgpLog(`fwModule('${m}') result:`, JSON.stringify(result));
      return result;
    };

    const [armModule] = fwModule('ARM');
    sgpLog('ARM module selected:', JSON.stringify(armModule));
    if (armModule) {
      const currentVersion = device.modules.ARM;
      const minVersion = armModule.MIN_VERSION;
      const latestVersion = armModule.LATEST_VERSION;

      const currentMinVersionCompare = this.compareVersions(currentVersion, minVersion);
      const currentLatestVersionCompare = this.compareVersions(currentVersion, latestVersion);

      sgpLog(`ARM compare: current='${currentVersion}', min='${minVersion}', latest='${latestVersion}'`);
      sgpLog(`ARM compare results: currentVsMin=${currentMinVersionCompare}, currentVsLatest=${currentLatestVersionCompare} (NOT_COMPARABLE=${FirmwareComparison.NOT_COMPARABLE})`);

      if (
        currentMinVersionCompare === FirmwareComparison.NOT_COMPARABLE ||
        currentLatestVersionCompare === FirmwareComparison.NOT_COMPARABLE
      ) {
        sgpLog('ARM: NOT_COMPARABLE → setting isUpdated=false, upgradePossible NOT set');
        upgradeLinks.ARM = null;
        isUpdated.ARM = false;
      } else {
        upgradeLinks.ARM = armModule.LINK;

        // upgradePossible: check MIN_VERSION (or bypass if configured)
        if (BYPASS_MIN_VERSION) {
          // Bypass active: always upgradable if LATEST_VERSION exists
          upgradePossible.ARM = !!latestVersion;
        } else {
          // Normal behavior
          upgradePossible.ARM = currentMinVersionCompare === FirmwareComparison.GREATER_THAN ||
            currentMinVersionCompare === FirmwareComparison.EQUAL;
        }

        // isUpdated: true only when current version equals latest version
        isUpdated.ARM = currentLatestVersionCompare === FirmwareComparison.EQUAL;
        upgradable = upgradePossible.ARM && !isUpdated.ARM;
        sgpLog(`ARM: upgradePossible=${upgradePossible.ARM}, isUpdated=${isUpdated.ARM}, upgradable=${upgradable}`);
      }
    } else {
      sgpLog('ARM: no matching module found!');
    }

    /**
     * DSPM, DSPS are always upgradable if ARM is upgradable
     * order of the checks is important
     */
    const [dspModule] = fwModule('DSPM');
    sgpLog('DSPM module selected:', JSON.stringify(dspModule));
    if (dspModule) {
      const currentVersion = device.modules.DSPM;
      const minVersion = dspModule.MIN_VERSION;
      const latestVersion = dspModule.LATEST_VERSION;

      const currentMinVersionCompare = this.compareVersions(currentVersion, minVersion);
      const currentLatestVersionCompare = this.compareVersions(currentVersion, latestVersion);

      sgpLog(`DSPM compare: current='${currentVersion}' (charCodes: ${Array.from(currentVersion).map(c => c.charCodeAt(0)).join(',')}), min='${minVersion}', latest='${latestVersion}'`);
      sgpLog(`DSPM compare results: currentVsMin=${currentMinVersionCompare}, currentVsLatest=${currentLatestVersionCompare} (NOT_COMPARABLE=${FirmwareComparison.NOT_COMPARABLE})`);

      // Debug the prefix comparison that happens inside compareVersions
      const cleanedCurrent = currentVersion.replace(/^0+/, '');
      const cleanedMin = minVersion.replace(/^0+/, '');
      const cleanedLatest = latestVersion.replace(/^0+/, '');
      sgpLog(`DSPM prefix debug: cleanedCurrent='${cleanedCurrent}' prefix='${cleanedCurrent.charAt(0)}', cleanedMin='${cleanedMin}' prefix='${cleanedMin.charAt(0)}', cleanedLatest='${cleanedLatest}' prefix='${cleanedLatest.charAt(0)}'`);

      if (
        currentMinVersionCompare === FirmwareComparison.NOT_COMPARABLE ||
        currentLatestVersionCompare === FirmwareComparison.NOT_COMPARABLE
      ) {
        sgpLog('DSPM: NOT_COMPARABLE → setting isUpdated=false, upgradePossible NOT set (THIS IS THE BUG PATH)');
        sgpLog(`DSPM: currentVsMin=${currentMinVersionCompare === FirmwareComparison.NOT_COMPARABLE ? 'NOT_COMPARABLE' : 'OK'}, currentVsLatest=${currentLatestVersionCompare === FirmwareComparison.NOT_COMPARABLE ? 'NOT_COMPARABLE' : 'OK'}`);
        upgradeLinks.DSPM = null;
        isUpdated.DSPM = false;
      } else {
        upgradeLinks.DSPM = dspModule.LINK;
        upgradePossible.DSPM = upgradePossible.ARM;

        // isUpdated: true only when current version equals latest version
        isUpdated.DSPM = currentLatestVersionCompare === FirmwareComparison.EQUAL;
        upgradable = upgradable || (upgradePossible.DSPM && !isUpdated.DSPM);
        sgpLog(`DSPM: upgradePossible=${upgradePossible.DSPM} (inherited from ARM), isUpdated=${isUpdated.DSPM}, upgradable=${upgradable}`);
      }
    } else {
      sgpLog('DSPM: no matching module found! device.modules.DSPM=', device.modules.DSPM);
    }

    const [dspsModule] = fwModule('DSPS');
    sgpLog('DSPS module selected:', JSON.stringify(dspsModule));
    if (dspsModule) {
      const currentVersion = device.modules.DSPS;
      const minVersion = dspsModule.MIN_VERSION;
      const latestVersion = dspsModule.LATEST_VERSION;

      const currentMinVersionCompare = this.compareVersions(currentVersion, minVersion);
      const currentLatestVersionCompare = this.compareVersions(currentVersion, latestVersion);

      sgpLog(`DSPS compare: current='${currentVersion}', min='${minVersion}', latest='${latestVersion}'`);
      sgpLog(`DSPS compare results: currentVsMin=${currentMinVersionCompare}, currentVsLatest=${currentLatestVersionCompare}`);

      if (
        currentMinVersionCompare === FirmwareComparison.NOT_COMPARABLE ||
        currentLatestVersionCompare === FirmwareComparison.NOT_COMPARABLE
      ) {
        sgpLog('DSPS: NOT_COMPARABLE → setting isUpdated=false, upgradePossible NOT set');
        upgradeLinks.DSPS = null;
        isUpdated.DSPS = false;
      } else {
        upgradeLinks.DSPS = dspsModule.LINK;
        upgradePossible.DSPS = upgradePossible.ARM;

        // isUpdated: true only when current version equals latest version
        isUpdated.DSPS = currentLatestVersionCompare === FirmwareComparison.EQUAL;
        upgradable = upgradable || (upgradePossible.DSPS && !isUpdated.DSPS);
        sgpLog(`DSPS: upgradePossible=${upgradePossible.DSPS}, isUpdated=${isUpdated.DSPS}, upgradable=${upgradable}`);
      }
    } else {
      sgpLog('DSPS: no matching module found!');
    }

    const latestVersions: {
      [moduleName: string]: string;
    } = {
      ARM: armModule?.LATEST_VERSION ?? '',
      DSPM: dspModule?.LATEST_VERSION ?? '',
      DSPS: dspsModule?.LATEST_VERSION ?? '',
    };

    sgpLog('=== FINAL RESULT ===');
    sgpLog('upgradePossible:', JSON.stringify(upgradePossible));
    sgpLog('isUpdated:', JSON.stringify(isUpdated));
    sgpLog('latestVersions:', JSON.stringify(latestVersions));
    sgpLog('upgradable:', upgradable);

    const config: UpgradeState = {
      upgradable,
      isUpdated,
      upgradePossible,
      upgradeLinks,
      latestVersions,
      safetyCustom: [],
    };

    if (deviceConfig.BATTERY) {
      if (deviceConfig.BATTERY.BMS?.length) {
        if (config.latestVersions) {
          config.latestVersions.BMS = deviceConfig.BATTERY.BMS[0].LATEST_VERSION;
        }
        if (config.upgradeLinks) {
          config.upgradeLinks.BMS = deviceConfig.BATTERY.BMS[0].LINK;
        }
        if (config.isUpdated) {
          config.isUpdated.BMS = false;
        }
        if (config.upgradePossible) {
          config.upgradePossible.BMS = true;
        }
      }

      if (deviceConfig.BATTERY.BDU?.length) {
        if (config.latestVersions) {
          config.latestVersions.BDU = deviceConfig.BATTERY.BDU[0].LATEST_VERSION;
        }
        if (config.upgradeLinks) {
          config.upgradeLinks.BDU = deviceConfig.BATTERY.BDU[0].LINK;
        }
        if (config.isUpdated) {
          config.isUpdated.BDU = false;
        }
        if (config.upgradePossible) {
          config.upgradePossible.BDU = true;
        }
      }

      if (deviceConfig.BATTERY.PCU?.length) {
        if (config.latestVersions) {
          config.latestVersions.PCU = deviceConfig.BATTERY.PCU[0].LATEST_VERSION;
        }
        if (config.upgradeLinks) {
          config.upgradeLinks.PCU = deviceConfig.BATTERY.PCU[0].LINK;
        }
        if (config.isUpdated) {
          config.isUpdated.PCU = false;
        }
        if (config.upgradePossible) {
          config.upgradePossible.PCU = true;
        }
      }
    }

    /**
     * SAFETY module is a special case, it is always upgradable & can have multiple versions (for different models)
     */
    const safetyModules = fwModule('SAFETY');
    if (safetyModules && safetyModules.length && safetyModules[0] !== null) {
      safetyModules.forEach((safetyModule) => {
        const safetyFileName = safetyModules.length > 1 ? `SAFETY_${safetyModule?.MODEL ?? ''}` : 'SAFETY';
        if (config.upgradeLinks) {
          config.upgradeLinks[safetyFileName] = safetyModule?.LINK ?? '';
        }
      });
      if (config.isUpdated) {
        config.isUpdated.SAFETY = false;
      }
      if (config.upgradePossible) {
        config.upgradePossible.SAFETY = true;
      }
      config.upgradable = true;
    }

    const customSafetyModules = deviceConfig.SAFETY_CUSTOM;
    if (customSafetyModules && customSafetyModules.length && customSafetyModules[0] !== null) {
      if (config.isUpdated) {
        config.isUpdated.SAFETY_CUSTOM = false;
      }
      if (config.upgradePossible) {
        config.upgradePossible.SAFETY_CUSTOM = true;
      }
      if (config.safetyCustom) {
        config.safetyCustom = customSafetyModules.map((safetyModule) => safetyModule?.LINK ?? '');
      }
      config.upgradable = true;
    }

    return config;
  }

  async download(selectedUpdateType: string): Promise<ArrayBuffer> {
    const url = this.state.upgradeConfig?.upgradeLinks?.[selectedUpdateType];

    if (!url) {
      throw new Error(`No URL found for the selected update type: ${selectedUpdateType}`);
    }

    // memoizedFetch now returns an arrayBuffer, so there's no need to call response.arrayBuffer()
    return await memoizedFetch(url);
  }

  async downloadSafety(safetyModel?: string) {
    const state = this.state;

    state.safetyModels = Object.keys(state.upgradeConfig?.upgradeLinks || {}).filter((key) => key.startsWith('SAFETY'));
    if (safetyModel && state.safetyModels.includes(safetyModel)) {
      state.selectedSafetyModel = safetyModel;
    } else {
      state.selectedSafetyModel = state.safetyModels[0];
    }

    try {
      if (
        (!state.selectedSafetyModel ||
          !state.upgradeConfig?.upgradeLinks ||
          !state.upgradeConfig?.upgradeLinks[state.selectedSafetyModel]) &&
        !state.upgradeConfig?.safetyCustom.length
      ) {
        return;
      }

      const safetyBin = state.selectedSafetyModel ? new Uint8Array(await this.download(state.selectedSafetyModel)) : null;
      const customSafety = await Promise.all(state.upgradeConfig?.safetyCustom.map((url) => memoizedFetch(url)));
      state.selectedSafetyModel = customSafety.length ? 'SAFETY_CUSTOM' : state.selectedSafetyModel;

      state.safetyMetadata = SgpInteraction.ExtractMetadataFromSafetyData(
        safetyBin,
        customSafety.map((s) => new Uint8Array(s)),
      );
      state.safetyList = state.safetyMetadata.map((item, index) => ({
        country: item.countryName,
        countryCode: item.countryCode.toString().padStart(3, '0'),
        region: item.regionName,
        regionCode: item.regionCode.toString().padStart(3, '0'),
        version: (item.parsedContent.ver as number).toString().padStart(3, '0'),
        index,
      }));
      state.filteredSafety = state.safetyList;
    } catch (error) {
      state.safetyMetadata = [];
    }

    this.state$.next(state);
  }

  async getUpgradeProgress(): Promise<UpgradeMultiModulesProgress | null> {
    if (!this.channelHandler.handler.channel.command) {
      return null;
    }

    try {
      const command = this.channelHandler.handler.channel.command;
      await command.executeMixedRequests([{
        mode: 'read',
        table: V3UpgradeInfo,
        composer: ComposeV3UpgradeInfo,
      }]);

      if (
        command.composedData.LOCAL_UPGRADE_STATUS_MODULE_NAME === undefined ||
        command.composedData.LOCAL_UPGRADE_STATUS_PROGRESS === undefined ||
        command.composedData.LOCAL_UPGRADE_STATUS_MODULE_CODE === undefined
      ) {
        return null;
      }

      return {
        name: command.composedData.LOCAL_UPGRADE_STATUS_MODULE_NAME as string,
        progress: command.composedData.LOCAL_UPGRADE_STATUS_PROGRESS as number,
        code: command.composedData.LOCAL_UPGRADE_STATUS_MODULE_CODE as number,
      };
    } catch (error) {
      sgpLog('getUpgradeProgress failed (channel likely disconnected)', error);
      return null;
    }
  }

  async startUpdate(selectedUpdateTypes: string[], opt: {
    waitToCheckInSecs: number;
    retryCountOnInitialCheck: number;
    progressPopup?: IonPopover;
    sessionId?: string;
    labels?: {
      upgradeFailed: string;
      updateSuccess: string;
      transferredModule: string;
    };
  }) {
    const defaultLabels = {
      upgradeFailed: 'Upgrade failed',
      updateSuccess: 'Update success',
      transferredModule: 'Transferred module',
    };
    opt.labels = { ...defaultLabels, ...opt.labels };

    const closeFn = this.channelHandler.handler.channel.close;
    (this.channelHandler.handler.channel as any).close = () => {
      try { opt.progressPopup?.dismiss(); } catch { }
      return closeFn();
    };

    this.cancellationSignal.cancelled = false;
    const state = this.state;
    state.multiModules = {
      modules: selectedUpdateTypes.map((name) => ({
        name,
        status: UpdateResult.waiting,
      })),
      current: null,
      upgrade: null,
      lastUpdated: new Date(),
    };

    const updateModuleStatus = (index: number, moduleStatus: {
      name: string;
      status: UpdateResult;
    }) => {
      if (!state.multiModules) {
        return;
      }

      state.multiModules.modules = state.multiModules.modules.map((module, _index) =>
        _index === index ? moduleStatus : module,
      );

      state.multiModules.lastUpdated = new Date();
    };

    for (let index = 0; index < selectedUpdateTypes.length; index++) {
      const selectedUpdateType = selectedUpdateTypes[index];
      const isLast = index === selectedUpdateTypes.length - 1;

      state.multiModules.current = { name: selectedUpdateType, status: UpdateResult.inProgress };
      updateModuleStatus(index, state.multiModules.current);

      // Log module start
      await this.upgradeLogger?.logEvent({
        operation: 'module_start',
        phase: `Starting ${selectedUpdateType}`,
        progress: 0,
        moduleId: selectedUpdateType,
      });

      this.state$.next(state);

      await this.updateProgress(
        selectedUpdateType === 'SAFETY_CONFIG' ? this.updateSafety : this.updateModule,
        { name: selectedUpdateType, isLast },
        opt.labels,
      );

      updateModuleStatus(index, state.multiModules.current);
      state.multiModules.current = null;
      this.state$.next(state);
    }

    if (state.errorMessage) {
      // Log upgrade failure
      await this.upgradeLogger?.logError({
        code: 'UPGRADE_FAILED',
        message: state.errorMessage,
        context: 'startUpdate completion'
      });
      await this.upgradeLogger?.failSession();

      opt.progressPopup?.dismiss();
      state.multiModules = null;
      this.toastFacade.present(opt.labels.upgradeFailed, state.errorMessage, {
        severity: 'error',
        browserMode: 'simple',
      });
      return;
    }

    /**
     * If only SAFETY_CONFIG is selected, we don't need to wait for the upgrade progress
     */
    if (selectedUpdateTypes.length === 1 && selectedUpdateTypes[0] === 'SAFETY_CONFIG') {
      opt.progressPopup?.dismiss();
      state.multiModules = null;
      await this.toastFacade.present(opt.labels.transferredModule, opt.labels.updateSuccess, {
        severity: 'success',
        browserMode: 'simple',
      });
      return;
    }

    let retryCount = 0;
    while (retryCount < opt.retryCountOnInitialCheck) {
      await wait(opt.waitToCheckInSecs);
      if (!this.channelHandler.handler.channel.command) {
        sgpLog('Channel disconnected during initial upgrade polling');
        break;
      }
      const upgradeProgress = await this.getUpgradeProgress();
      if (upgradeProgress) {
        state.multiModules.upgrade = upgradeProgress;
        this.state$.next(state);
      } else {
        retryCount++;
      }
    }

    while (state.multiModules?.upgrade?.progress || state.multiModules?.upgrade?.code) {
      await wait(opt.waitToCheckInSecs * 1000);
      if (!this.channelHandler.handler.channel.command) {
        sgpLog('Channel disconnected during upgrade polling');
        break;
      }
      const upgradeProgress = await this.getUpgradeProgress();
      if (upgradeProgress) {
        state.multiModules.upgrade = upgradeProgress;
        this.state$.next(state);
      }
    }

    opt.progressPopup?.dismiss();
    state.multiModules = null;
    await this.toastFacade.present(opt.labels.transferredModule, opt.labels.updateSuccess, {
      severity: 'success',
      browserMode: 'simple',
    });
  }

  async updateProgress(
    updateFn: (selectedUpdateType: {
      name: string;
      isLast: boolean;
    }) => Promise<void>,
    selectedUpdateType: {
      name: string;
      isLast: boolean;
    },
    labels: {
      transferredModule: string;
      updateSuccess: string;
    },
  ) {
    const state = this.state;

    state.isUpdating = true;
    state.errorMessage = null;
    state.currentPhase = 0;
    state.totalProgress = 0;

    this.state$.next(state);

    try {
      await updateFn(selectedUpdateType);

      if (!state.errorMessage) {
        await this.toastFacade.present(labels.transferredModule, labels.updateSuccess, {
          severity: 'success',
          browserMode: 'simple',
        });
      }

      if (state.multiModules?.current) {
        state.multiModules.current.status = UpdateResult.success;
      }
    } catch (e) {
      if (e instanceof Error) {
        state.errorMessage = e.message;
      }

      if (state.multiModules?.current) {
        state.multiModules.current.status = UpdateResult.failed;
      }
    } finally {
      state.isUpdating = false;
      state.updatePhases = [];
    }

    this.state$.next(state);
  }

  async updateSafety() {
    const state = this.state;
    sgpLog('updateSafety START');

    if (state.selectedSafetyIndex === null) {
      throw new Error('No safety selected');
    }

    if (!state.safetyMetadata) {
      throw new Error('No safety metadata');
    }

    const safetyM = state.safetyMetadata[state.selectedSafetyIndex];
    sgpLog(`updateSafety: safetyIndex=${state.selectedSafetyIndex}, blocks=${safetyM.parsedContent.blocks ? Object.keys(safetyM.parsedContent.blocks).length : 'undefined'}`);

    if (safetyM.parsedContent.blocks === undefined) {
      throw new Error('Invalid safety data');
    }


    const command = this.channelHandler.handler.channel.command;
    sgpLog(`updateSafety: command=${!!command}, channelType=${this.channelHandler.handler.type}, channel=${this.channelHandler.handler.channel.constructor.name}`);
    if(!command) {
      throw new Error('No command');
    }

    sgpLog('updateSafety: calling safetyService.upgrade...');
    await this.safetyService.upgrade(safetyM, command, (
      p: number, // progress in %
      f: number, // phase
      e: string, // error message
      z: string[], // phase names
    ) => {
      state.totalProgress = p;
      state.currentPhase = f;
      state.errorMessage = e;
      state.updatePhases = z;

      if (state.errorMessage) {
        throw new Error(state.errorMessage);
      }

      this.state$.next(state);
    }, this.cancellationSignal);
  }

  async updateModule(selectedUpdateType: {
    name: string;
    isLast: boolean;
  }) {
    const state = this.state;

    const file = await this.download(selectedUpdateType.name);
    if (!file) {
      throw new Error('Error downloading firmware update');
    }
    const firmwareMetadata = SgpInteraction.ExtractMetadataFromFirmware(new Uint8Array(file));

    if (!firmwareMetadata) {
      throw new Error('Error extracting firmware metadata');
    }

    const command = this.channelHandler.handler.channel.command;
    const modbusAddress = command?.options.modbusAddress ?? 1;
    const deviceId = command?.options.deviceId;

    // Use SgpInteractionWithLogging to capture raw commands/responses
    // Pass deviceId so SGP shares the same scheduler as ModbusLink on the same bus
    const interaction = this.upgradeLogger
      ? new SgpInteractionWithLogging(
          this.channelHandler.handler.channel.interaction,
          modbusAddress,
          this.upgradeLogger,
          selectedUpdateType.name,
          deviceId,
        )
      : new SgpInteraction(this.channelHandler.handler.channel.interaction, modbusAddress, deviceId);

    sgpLog('SGP Interaction created with logging:', !!this.upgradeLogger);
    await interaction.upgradeInNormalMode(firmwareMetadata, file, selectedUpdateType.isLast, (
      p: number, // progress in %
      f: number, // phase
      e: string, // error message
      z: string[], // phase names
    ) => {
      // Log phase change (only when phase changes, not for every progress update)
      const previousPhase = state.currentPhase;

      state.totalProgress = p;
      state.currentPhase = f;
      state.errorMessage = e;
      state.updatePhases = z;

      if (f !== previousPhase) {
        this.upgradeLogger?.logEvent({
          operation: 'phase_change',
          phase: z[f] || `Phase ${f}`,
          progress: p,
          moduleId: selectedUpdateType.name,
        });
      }

      if (state.errorMessage) {
        this.upgradeLogger?.logError({
          code: 'MODULE_UPDATE_ERROR',
          message: state.errorMessage,
          context: `Module: ${selectedUpdateType.name}, Phase: ${f}`
        }, selectedUpdateType.name);
        throw new Error(state.errorMessage);
      }

      this.state$.next(state);
    }, this.cancellationSignal);
  }

  cancelUpdate() {
    this.cancellationSignal.cancelled = true;
    // Log cancellation
    this.upgradeLogger?.cancelSession();
  }
}
